import React, { ReactElement, memo } from "react";
import { FlashList, ListRenderItem } from "@/components/atoms/FlashList";
import { RefreshControl } from "@/components/atoms/RefreshControl";

import ItemSeparator from "@/components/molecules/ItemSeparator";
import { invitationsListContentContainerStyle } from "@/features/invitations/constants";
import { InvitationsOrganizationUserFragment } from "@/generated/graphql";

/**
 * Props for the UsersView component.
 */
interface Props {
  organizationUsers: InvitationsOrganizationUserFragment[];
  renderItem: ListRenderItem<InvitationsOrganizationUserFragment>;
  refreshing: boolean;
  widthContent: number;
  onLoadMore: () => void;
  onRefresh: () => void;
  renderListFooterComponent: () => ReactElement | null;
  renderListEmptyComponent: () => ReactElement | null;
}
const keyExtractor = (item: InvitationsOrganizationUserFragment) => item.id;

/**
 * View component for displaying the organization users list with pagination and pull-to-refresh.
 * @param root0 - The component props
 * @param root0.organizationUsers - Array of organization user data fragments to display in the list
 * @param root0.renderItem - Function to render each individual organization user item in the list
 * @param root0.refreshing - Boolean indicating if pull-to-refresh operation is in progress
 * @param root0.widthContent - The content width for responsive layout sizing
 * @param root0.onLoadMore - Callback function to load more users when scrolling reaches the end
 * @param root0.onRefresh - Callback function to refresh the users list
 * @param root0.renderListFooterComponent - Function to render custom footer component for pagination or loading states
 * @param root0.renderListEmptyComponent - Function to render custom empty state when no users exist
 */
const UsersView = ({
  organizationUsers,
  renderItem,
  refreshing,
  widthContent,
  onLoadMore,
  onRefresh,
  renderListFooterComponent,
  renderListEmptyComponent,
}: Props) => (
  <FlashList
    data={organizationUsers}
    scrollEventThrottle={16}
    refreshing={refreshing}
    contentContainerStyle={invitationsListContentContainerStyle(widthContent)}
    renderItem={renderItem}
    keyExtractor={keyExtractor}
    ListFooterComponent={renderListFooterComponent}
    ListEmptyComponent={renderListEmptyComponent}
    onEndReached={onLoadMore}
    onRefresh={onRefresh}
    refreshControl={
      <RefreshControl
        refreshing={refreshing}
        onRefresh={onRefresh}
        colors={["white"]}
        tintColor="white"
      />
    }
    ItemSeparatorComponent={ItemSeparator}
  />
);

UsersView.displayName = "UsersView";

export default memo(UsersView);
